import React, { useState, useEffect } from 'react';
import { useGame } from '../contexts/GameContext';
import { getWebSocketUrl } from '../utils/config';
import ThemeToggle from './ThemeToggle';

const MainMenu: React.FC = () => {
  const { connectToServer, joinGame, gameState, lastError, clearError, getSavedSession } = useGame();
  const [playerName, setPlayerName] = useState(() => {
    // Pre-fill from localStorage if available
    return localStorage.getItem('liarsDice_playerName') || '';
  });
  const [gameId, setGameId] = useState(() => {
    // Pre-fill from URL fragment if available
    const fragment = window.location.hash.substring(1); // Remove the '#'
    return fragment.toUpperCase();
  });
  const [isConnecting, setIsConnecting] = useState(true);
  const [isConnected, setIsConnected] = useState(false);

  // Auto-connect on mount and try to reconnect if session exists
  useEffect(() => {
    let isMounted = true;

    const connect = async () => {
      const serverUrl = getWebSocketUrl();
      console.log('Connecting to:', serverUrl);

      setIsConnecting(true);
      try {
        // connectToServer checks if already connected (prevents double connection in StrictMode)
        await connectToServer(serverUrl);

        if (!isMounted) return;

        setIsConnected(true);

        // Check if we should auto-join from URL fragment
        const fragment = window.location.hash.substring(1);
        const savedPlayerName = localStorage.getItem('liarsDice_playerName');

        console.log('Auto-join check - Fragment:', fragment, 'Saved name:', savedPlayerName);

        if (fragment && savedPlayerName) {
          const normalizedGameId = fragment.toUpperCase();

          // Check if we have a saved playerId for this specific game
          const savedSession = getSavedSession();
          const savedPlayerId = savedSession?.gameId === normalizedGameId ? savedSession.playerId : undefined;

          console.log('Auto-joining from URL fragment:', normalizedGameId, 'with name:', savedPlayerName, 'playerId:', savedPlayerId);
          joinGame(savedPlayerName, normalizedGameId, savedPlayerId);
        }
      } catch (error) {
        console.error('Connection failed:', error);
        if (isMounted) {
          setIsConnected(false);
        }
      } finally {
        if (isMounted) {
          setIsConnecting(false);
        }
      }
    };

    connect();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleJoinGame = () => {
    if (!playerName.trim()) return;

    // Check if there's a saved session for this game to enable rejoin
    const savedSession = getSavedSession();
    const savedPlayerId = savedSession?.gameId === gameId.trim().toUpperCase() ? savedSession.playerId : undefined;

    joinGame(playerName.trim(), gameId.trim().toUpperCase() || undefined, savedPlayerId);
  };

  const handleGameIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Always convert to uppercase
    setGameId(e.target.value.toUpperCase());
  };

  if (gameState) {
    return null; // GameRoom will be shown instead
  }

  return (
    <div className="min-h-screen bg-surface-100 dark:bg-gray-900 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 sm:p-8 w-full max-w-md relative">
        {/* Theme toggle in top-right corner */}
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>

        <div className="text-center mb-4 sm:mb-6 pr-12">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-1">Liar's Dice</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Bluff your way to victory</p>
        </div>

        {lastError && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg mb-4">
            <div className="flex justify-between items-start">
              <p className="text-sm">{lastError}</p>
              <button
                onClick={clearError}
                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 ml-2 font-bold"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {isConnecting ? (
          <div className="text-center py-6">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mb-3"></div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Connecting...</p>
          </div>
        ) : !isConnected ? (
          <div className="text-center py-6">
            <div className="text-red-500 text-4xl mb-3">⚠️</div>
            <p className="text-gray-900 dark:text-white font-semibold mb-1">Connection Failed</p>
            <p className="text-gray-600 dark:text-gray-400 text-xs mb-3">
              Unable to connect. Please refresh.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-primary-500 hover:bg-primary-600 text-white font-semibold py-2 px-4 text-sm rounded-lg transition-colors shadow-md hover:shadow-lg"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-xs font-medium mb-1.5">
                Your Name
              </label>
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleJoinGame()}
                className="w-full px-3 py-2 text-sm bg-surface-50 dark:bg-gray-700 border border-surface-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:border-primary-500 dark:focus:border-primary-400 focus:ring-1 focus:ring-primary-200 dark:focus:ring-primary-800"
                placeholder="Enter your name"
                maxLength={20}
                autoFocus
              />
            </div>

            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-xs font-medium mb-1.5">
                Game ID (optional)
              </label>
              <input
                type="text"
                value={gameId}
                onChange={handleGameIdChange}
                onKeyPress={(e) => e.key === 'Enter' && handleJoinGame()}
                className="w-full px-3 py-2 text-sm bg-surface-50 dark:bg-gray-700 border border-surface-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:border-primary-500 dark:focus:border-primary-400 focus:ring-1 focus:ring-primary-200 dark:focus:ring-primary-800 uppercase"
                style={{ textTransform: 'uppercase' }}
              />
            </div>

            <button
              onClick={handleJoinGame}
              disabled={!playerName.trim()}
              className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-surface-400 dark:disabled:bg-gray-600 text-white font-semibold py-2.5 px-4 text-sm rounded-lg transition-colors shadow-md hover:shadow-lg disabled:cursor-not-allowed"
            >
              {gameId ? 'Join Game' : 'Create New Game'}
            </button>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-surface-200 dark:border-gray-700">
          <h3 className="text-gray-900 dark:text-white font-semibold text-xs mb-1.5">How to Play</h3>
          <ul className="text-gray-600 dark:text-gray-400 text-xs space-y-0.5">
            <li>• Start with 5 dice</li>
            <li>• Bid on total dice values</li>
            <li>• Challenge lies</li>
            <li>• Last player wins!</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MainMenu;
